import axios from 'axios'
import { useAuthStore } from '../../stores/authStore'

// Global flag to track if we're currently refreshing the token
let isRefreshing = false
// Global flag to track if we're redirecting to login (prevents multiple redirects)
let isRedirectingToLogin = false
// Queue to hold requests while token is being refreshed
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (error?: any) => void }> = []

/**
 * Reset auth interceptor state. Must be called after a fresh login/register
 * to clear any stale flags from a previous failed auth cycle.
 */
export const resetAuthInterceptorState = () => {
  isRefreshing = false
  isRedirectingToLogin = false
  failedQueue = []
}

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

const redirectToLogin = () => {
  if (isRedirectingToLogin) {
    return
  }
  isRedirectingToLogin = true
  console.warn('[Auth] Redirecting to login page')
  
  // Clear the queue with error
  processQueue(new Error('Authentication failed'), null)
  
  // Clear Auth Store - Important: This must happen before redirect
  // We'll also try to clear localStorage manually as a backup
  try {
    useAuthStore.getState().logout()
    localStorage.removeItem('auth-storage')
  } catch (e) {
    console.error('[Auth] Failed to clear auth store:', e)
  }

  // Use a hard redirect to ensure the app state is completely reset
  window.location.replace('/login')
}

declare global {
  interface Window {
    __DASHBAL_CONFIG__?: {
      apiBaseUrl?: string
      wsUrl?: string
      skipEmailVerification?: boolean
      enableDebug?: boolean
      isProduction?: boolean
      tiktokClientKey?: string
      facebookAppId?: string
      facebookConfigId?: string
      googleClientId?: string
      appleClientId?: string
    }
  }
}

// Get API base URL from runtime config (injected by server.js)
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.__DASHBAL_CONFIG__?.apiBaseUrl) {
    return window.__DASHBAL_CONFIG__.apiBaseUrl
  }
  if (process.env.API_BASE_URL) {
    return process.env.API_BASE_URL
  }
  if (process.env.NODE_ENV !== 'production') {
    return 'http://localhost:3001'
  }
  return window.location.origin
}

// Create axios instance with base configuration
export const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    if (isRedirectingToLogin) {
      return Promise.reject(new Error('Redirecting to login'))
    }

    const { tokens } = useAuthStore.getState()
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle token refresh and errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isRedirectingToLogin) {
      return Promise.reject(new Error('Redirecting to login'))
    }

    const originalRequest = error.config
    const responseData = error.response?.data
    const status = error.response?.status

    // Avoid loops and skip for special routes
    if (originalRequest?._retry || 
        originalRequest?.url?.includes('/auth/login') || 
        originalRequest?.url?.includes('/auth/register')) {
      return Promise.reject(error)
    }

    // 1. Check for banned/deleted user
    const errorMessage = responseData?.error || responseData?.message || ''
    const errorCode = responseData?.code

    // We only redirect to login if the account itself is inactive or not found.
    // We must NOT redirect on 403 if it's a plan limit (LIMIT_REACHED) or 
    // a standard access denial (FORBIDDEN, BOARD_ACCESS_DENIED), as these
    // are valid responses for an active logged-in session.
    const isUserBlocked = (status === 403 && (
      errorCode === 'AUTH_ACCOUNT_INACTIVE' ||
      errorMessage.includes('account is inactive') || 
      errorMessage.includes('deactivated')
    )) || (status === 404 && errorCode === 'USER_NOT_FOUND')

    if (isUserBlocked && status !== 401) {
      console.warn('[AuthInterceptor] Access blocked (account inactive or not found)')
      redirectToLogin()
      return Promise.reject(error)
    }

    // 2. Handle Token Expiration (401)
    if (status === 401) {
      // If 401 happens on refresh endpoint itself, we are done
      if (originalRequest?.url?.includes('/auth/refresh')) {
        console.warn('[AuthInterceptor] Refresh token expired or invalid')
        redirectToLogin()
        return Promise.reject(error)
      }

      if (isRefreshing) {
        console.log('[AuthInterceptor] Already refreshing, queuing:', originalRequest.url)
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return apiClient(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      console.log('[AuthInterceptor] 401 detected, attempting refresh for:', originalRequest.url)
      originalRequest._retry = true
      isRefreshing = true

      try {
        const { tokens: currentTokens } = useAuthStore.getState()
        if (!currentTokens?.refreshToken) {
          throw new Error('No refresh token available')
        }

        // Use a dedicated instance for refresh to avoid interceptor recursion
        const refreshInstance = axios.create({
          baseURL: getApiBaseUrl(),
          timeout: 10000,
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        })

        const response = await refreshInstance.post('/auth/refresh', {
          refreshToken: currentTokens.refreshToken
        })

        if (response.data?.success && response.data?.data?.tokens) {
          const newTokens = response.data.data.tokens
          console.log('[AuthInterceptor] Refresh successful')
          
          useAuthStore.getState().setTokens(newTokens)
          isRefreshing = false
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`
          processQueue(null, newTokens.accessToken)
          return apiClient(originalRequest)
        } else {
          throw new Error('Refresh response invalid')
        }
      } catch (refreshErr: any) {
        console.error('[AuthInterceptor] Refresh failed:', refreshErr.message)
        isRefreshing = false
        redirectToLogin()
        return Promise.reject(refreshErr)
      }
    }

    if (status >= 500) {
      console.error('[API] Server error:', responseData?.error || error.message)
    }

    return Promise.reject(error)
  }
)

// API response types
export interface ApiResponse<T = any> {
  success: boolean
  data: T
  message?: string
  error?: string
}

// Helper function to handle API responses
export const handleApiResponse = <T>(response: any): ApiResponse<T> => {
  if (response.data && typeof response.data === 'object') {
    return response.data
  }
  throw new Error('Invalid API response format')
}

// User-friendly error messages
export const handleApiError = (error: any): string => {
  const status = error.response?.status
  if (status >= 500) return 'Server error. Please try again later.'
  if (!error.response) return 'Connection issue. Please check your internet.'
  if (status === 429) return 'Too many requests. Slow down.'
  if (status === 401) return 'Session expired. Please log in again.'
  if (status === 402) return 'Billing inactive. Restore your subscription to make changes.'
  if (status === 403) return 'Access denied.'
  if (status === 404) return 'Resource not found.'
  
  const serverMsg = error.response?.data?.error || error.response?.data?.message
  if (serverMsg && !containsTechnicalTerms(serverMsg)) return serverMsg
  
  return 'Requested operation failed.'
}

const containsTechnicalTerms = (message: string): boolean => {
  const terms = ['sql', 'prisma', 'database', 'stack', 'undefined', 'null', 'internal server error']
  const lower = message.toLowerCase()
  return terms.some(t => lower.includes(t))
}
